import {
  getErrorStatus,
  isAbortError,
  isServerErrorStatus,
  isStatus,
} from "@/core/api/http-error.utils";

/**
 * Only a 404 from the assessment endpoint definitively means "this assessment
 * does not exist any more" — that is the single case in which it is safe to
 * wipe the local session and start the user over.
 *
 * 401 is intentionally NOT resettable here. A 401 can be triggered by transient
 * clock skew between the issuing pod and the validating pod, and resetting in
 * that case ejects users mid-flow. 401s are handled by the consumer via the
 * `reauthenticate` capability on the session context: refresh the token first,
 * and only treat the failure as terminal after that path has been exhausted.
 */
export const isResettableAssessmentError = (error: unknown): boolean => {
  return isStatus(error, 404);
};

/**
 * Signals a token-validity failure on the assessment endpoint. Use this to drive
 * a re-authentication attempt before falling back to a full reset.
 */
export const isAuthenticationFailureError = (error: unknown): boolean => {
  return isStatus(error, 401);
};

export const isRetryableAssessmentError = (error: unknown): boolean => {
  if (isAbortError(error)) return false;

  const status = getErrorStatus(error);

  // Network/runtime errors may not include an HTTP status and can be transient.
  if (typeof status !== "number") {
    return true;
  }

  return isServerErrorStatus(status);
};

export const shouldSurfaceAssessmentError = (error: unknown): boolean => {
  if (!error) {
    return false;
  }

  if (isAbortError(error)) return false;

  // 404 is handled by reset, 401 is handled by reauthenticate-then-reset. Anything
  // else (5xx, parse errors, unexpected statuses) should reach the route's
  // `error.tsx` boundary so the user sees a real error UI instead of an opaque
  // redirect away from their flow.
  return !isResettableAssessmentError(error) && !isAuthenticationFailureError(error);
};
